import React, { useEffect, useState } from "react";
import cx from "classnames";
import { Icon, arrowDown, arrowUp, arrowLeft, arrowRight } from "Icons/index";
import Loader from "Components/loader/Loader";
import { Card, Badge, BadgeDelta, BarChart, List, DonutChart, Legend, ListItem, AreaChart } from "@tremor/react";
import QboxmailApi from "@/qboxmailApi";

const api = new QboxmailApi({ useCookie: true });

function AngelIcon(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" {...props}>
      <g
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        color="currentColor"
      >
        <path d="M3.078 7.5A9.9 9.9 0 0 0 2 12.015C2 17.529 6.477 22 12 22s10-4.47 10-9.985A9.9 9.9 0 0 0 20.922 7.5"></path>
        <path d="M8 15a5 5 0 0 0 4 2a5 5 0 0 0 4-2"></path>
        <ellipse cx="12" cy="4" rx="10" ry="2"></ellipse>
        <path d="M7 10.5a1.5 1.5 0 0 1 3 0m4 0a1.5 1.5 0 0 1 3 0"></path>
      </g>
    </svg>
  );
}

function EvilIcon(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" {...props}>
      <path
        fill="currentColor"
        d="M1.5 2.09c.9.91 2.37 1.64 4.19 2.16A9.92 9.92 0 0 1 12 2c2.39 0 4.59.84 6.31 2.25C20.13 3.73 21.6 3 22.5 2.09c-.03 1.63-.85 3.12-2.22 4.31A9.9 9.9 0 0 1 22 12a10 10 0 0 1-10 10A10 10 0 0 1 2 12c0-2.08.63-4 1.72-5.6C2.35 5.21 1.53 3.72 1.5 2.09M20 12a8 8 0 0 0-8-8a8 8 0 0 0-8 8a8 8 0 0 0 8 8a8 8 0 0 0 8-8m-9.5-2c0 .8-.7 1.5-1.5 1.5s-1.5-.7-1.5-1.5V8.5zm6 0c0 .8-.7 1.5-1.5 1.5s-1.5-.7-1.5-1.5l3-1.5zM12 17.23c-1.75 0-3.29-.73-4.19-1.81L9.23 14c.45.72 1.52 1.23 2.77 1.23s2.32-.51 2.77-1.23l1.42 1.42c-.9 1.08-2.44 1.81-4.19 1.81"
      ></path>
    </svg>
  );
}

function formatDate(dateString) {
  const date = new Date(dateString);
  const options = { day: "numeric", month: "long" };
  return new Intl.DateTimeFormat("en-GB", options).format(date);
}

const formatDailySentData = (timeSpans) => {
  let all = [];

  let h = 0;
  for (let sent of timeSpans) {
    all.push({ hour: String(h).padStart(2, "0") + ":00", sent: sent });
    h++;
  }

  return all;
};

const ReputationChanges = ({ reputationChanges }) => {
  const days = Object.keys(reputationChanges);

  const [dayIndex, setDayIndex] = useState(0);
  const [openedDetails, setOpenedDetails] = useState({});

  const changes = reputationChanges?.[days[dayIndex]];

  const next = () => {
    let n = dayIndex - 1;
    if (!days[n]) return dayIndex;
    setDayIndex(n);
  };

  const prev = () => {
    let n = dayIndex + 1;
    if (!days[n]) return dayIndex;
    setDayIndex(n);
  };

  if (!changes) {
    return <div className="w-full text-sm text-center">No reputation data available</div>;
  }

  return (
    <div>
      <div className="flex mb-4">
        <div
          onClick={prev}
          className={cx("flex items-center justify-center p-2 border border-gray-200 border-solid rounded", {
            "opacity-50 cursor-not-allowed ": !days[dayIndex + 1],
            "cursor-pointer": days[dayIndex + 1],
          })}
        >
          <Icon svg={arrowLeft} width=".5rem" height=".5rem" />
        </div>

        <div className="flex items-center justify-center flex-grow w-full !text-base sectionTitle">
          {formatDate(days[dayIndex])} - {reputationChanges[days[dayIndex]].length} utenze con invii
        </div>

        <div
          onClick={next}
          className={cx("flex items-center justify-center p-2 border border-gray-200 border-solid rounded", {
            "opacity-50 cursor-not-allowed ": !days[dayIndex - 1],
            "cursor-pointer": days[dayIndex - 1],
          })}
        >
          <Icon svg={arrowRight} width=".5rem" height=".5rem" />
        </div>
      </div>

      <div className="">
        {changes.map((d) => {
          return (
            <div className="border border-[--bg-d5] border-solid" key={d.user}>
              <div className="flex justify-between px-4 py-2 bg-white">
                <div className="flex items-center gap-3">
                  <div className="flex flex-col items-center gap-1">
                    <Badge className="w-full" tooltip="Messaggi inviati">
                      {" "}
                      {d.rawData.messagesSent}
                    </Badge>

                    <BadgeDelta
                      className="w-20 h-6 text-xs"
                      deltaType={
                        d.reputationChanged
                          ? d.reputation_change_direction === "up"
                            ? "increase"
                            : "decrease"
                          : "unchanged"
                      }
                      isIncreasePositive={true}
                      tooltip={
                        d.reputationChanged
                          ? d.reputation_change_direction === "up"
                            ? "La reputazione è migliorata"
                            : "La reputazione è peggiorata"
                          : "La reputazione è stata mantenuta"
                      }
                    >
                      {d.reputation}
                    </BadgeDelta>
                  </div>

                  <div className="text-sm">
                    <div className="text-xs text-[--text-a60]">
                      {d.account?.customer_code} - {d.account?.customer_fullname}
                    </div>
                    {d.account?.id || "<no id found>"} - {d.user} - {d.account?.description || "<no description found>"}
                  </div>
                </div>

                <div
                  onClick={() => {
                    setOpenedDetails({ ...openedDetails, [d.user]: !openedDetails[d.user] });
                  }}
                  className={cx(
                    "flex items-center justify-center p-2 border border-gray-200 border-solid rounded cursor-pointer"
                  )}
                >
                  <Icon svg={openedDetails[d.user] ? arrowUp : arrowDown} width=".5rem" height=".5rem" />
                </div>

                {/* {d.account?.id} - {d.user} */}
                {/* {d.account?.description} - {d.rawData.messagesSent} messaggi inviati */}

                {/* <div
                    class={cx("text-sm flex gap-1 items-center px-2 . py-1 rounded border-solid border ", {
                      "bg-green-50 text-green-500 border-green-400": d.rawData.evilPerc < 5,
                      "bg-yellow-50 text-yellow-500 border-yellow-400":
                        d.rawData.evilPerc >= 5 && d.rawData.evilPerc < 15,
                      "bg-red-50 text-red-500 border-red-400": d.rawData.evilPerc >= 15,
                    })}
                  >
                    {d.rawData.evilPerc < 5 ? <AngelIcon /> : <EvilIcon />}
                    {d.rawData.evilPerc}%
                  </div> */}
              </div>

              {openedDetails[d.user] && (
                <div className="grid grid-cols-3 px-4 py-4 leading-6 gap-x-4">
                  <h3 className="col-span-3 mb-2 text-lg font-semibold text-black">
                    Messaggi inviati nelle 24 ore: {d.rawData.messagesSent}
                  </h3>
                  <div className="col-span-2">
                    <Card className="w-full">
                      <BarChart
                        className="h-80"
                        data={formatDailySentData(d.rawData.timeSpans)}
                        index="hour"
                        categories={["sent"]}
                        colors={["#2c97ff"]}
                      />
                    </Card>
                  </div>

                  <Card className="py-4 text-sm">
                    <List>
                      <h4 className="mb-2">Dettagli destinatari</h4>
                      <ListItem className="py-0 pb-1">
                        <span>Free provider</span>
                        <span>
                          {d.rawData.freeProviders} ({d.rawData.freeProvidersPerc}%)
                        </span>
                      </ListItem>
                      <ListItem className="py-0 pb-1">
                        <span>Indirizzi generici (es: info@)</span>
                        <span>
                          {d.rawData.genericAddresses} ({d.rawData.genericAddressesPerc}%)
                        </span>
                      </ListItem>

                      <h4 className="mt-4 mb-2">Dettaglio piano</h4>
                      <ListItem className="py-0 pb-1">
                        <span>Tipologia dichiarata</span>
                        <span>{d.rawData.accountType}</span>
                      </ListItem>

                      <ListItem className="py-0 pb-1">
                        <span>Pattern di invio rilevata</span>
                        <span
                          className={cx({
                            "text-green-500": d.rawData.detectedPattern === d.rawData.accountType,
                            "text-red-500": d.rawData.detectedPattern !== d.rawData.accountType,
                          })}
                        >
                          {d.rawData.detectedPattern}
                        </span>
                      </ListItem>

                      <h4 className="mt-4 mb-2">Reputazione</h4>
                      <ListItem className="py-0 pb-1">
                        <span>Nuova reputazione</span>
                        <span>{d.reputation}</span>
                      </ListItem>

                      <ListItem className="py-0 pb-1">
                        <span>Reputazione precedente</span>
                        <span>{d.previousReputation || "-"}</span>
                      </ListItem>

                      <ListItem className="py-0 pb-1">
                        <span>Evil score</span>
                        <div
                          class={cx("text-sm flex gap-1 items-center px-2 . py-1 rounded border-solid border h-6", {
                            "bg-green-50 text-green-500 border-green-400": d.rawData.evilPerc < 5,
                            "bg-yellow-50 text-yellow-500 border-yellow-400":
                              d.rawData.evilPerc >= 5 && d.rawData.evilPerc < 15,
                            "bg-red-50 text-red-500 border-red-400": d.rawData.evilPerc >= 15,
                          })}
                        >
                          {d.rawData.evilPerc < 5 ? <AngelIcon /> : <EvilIcon />}
                          {d.rawData.evilPerc}%
                        </div>
                      </ListItem>

                      <ListItem className="py-0 pb-1">
                        <span>Indirizzi in lista di soppressione</span>
                        <div>{d.rawData.suppressedRecipients || "-"}</div>
                      </ListItem>
                    </List>
                  </Card>

                  <div className="flex items-center col-span-3 gap-4 mt-4">
                    <div>
                      <h3 className="mb-2 text-lg font-semibold text-black">Dettaglio ricezioni</h3>

                      <Card className="flex flex-col gap-3">
                        <DonutChart
                          variant="pie"
                          className="w-content"
                          data={[
                            {
                              name: "Delivered",
                              value: d.rawData.messagesSent - d.rawData.messagesBounced,
                            },
                            { name: "Bounced", value: d.rawData.messagesBounced },
                          ]}
                          colors={["green-400", "red-500"]}
                        />

                        <Legend
                          categories={[
                            `Delivered ${d.rawData.messagesSent - d.rawData.messagesBounced}`,
                            `Bounced ${d.rawData.messagesBounced}`,
                          ]}
                          colors={["green-400", "red-500"]}
                        />
                      </Card>
                    </div>

                    {d.rawData.messagesBounced > 0 && (
                      <div>
                        <h3 className="mb-2 text-lg font-semibold text-black">Dettaglio tipologie di bounce</h3>

                        <Card>
                          <DonutChart
                            variant="pie"
                            data={Object.entries(d.rawData.categories).map(([k, v]) => ({
                              name: k,
                              value: v,
                            }))}
                            colors={[
                              "blue",
                              "cyan",
                              "indigo",
                              "violet",
                              "fuchsia",
                              "gray",
                              "yellow",
                              "green",
                              "emerald",
                              "amber",
                              "orange",
                              "teal",
                              "purple",
                              "rose",
                            ]}
                          />
                          <Legend
                            className="mt-3"
                            categories={Object.entries(d.rawData.categories).map(([k, v]) => `${k} - ${v}`)}
                            colors={[
                              "blue",
                              "cyan",
                              "indigo",
                              "violet",
                              "fuchsia",
                              "gray",
                              "yellow",
                              "green",
                              "emerald",
                              "amber",
                              "orange",
                              "teal",
                              "purple",
                              "rose",
                            ]}
                          />
                        </Card>
                      </div>
                    )}
                  </div>

                  <div class="col-span-2 mt-2">
                    <h3 className="mb-2 text-lg font-semibold text-black">
                      Crescita lista di soppressione ultime 10 settimane
                    </h3>
                    <Card>
                      {d.rawData.suppressedRecipientsWeekly ? (
                        <AreaChart
                          data={d.rawData.suppressedRecipientsWeekly.map((s) => {
                            return {
                              "Indirizzi in lista di soppressione": s,
                            };
                          })}
                          yAxisWidth={65}
                          categories={["Indirizzi in lista di soppressione"]}
                          colors={["indigo"]}
                        />
                      ) : (
                        <div className="text-sm opacity-50">Dati non disponibli</div>
                      )}
                    </Card>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AdminDeliveryDashboard = () => {
  const [reputationChanges, setReputationChanges] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.getDeliveryReputationChanges().then((res) => {
      setReputationChanges(res.reputation_changes_by_day);
      setLoading(false);
    });
  }, []);

  if (loading) return <Loader />;

  return (
    <div>
      <ReputationChanges reputationChanges={reputationChanges}></ReputationChanges>
    </div>
  );
};

export default AdminDeliveryDashboard;
